import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Text } from '@mantine/core';

/**
 * A label that turns into a text field when clicked.
 *
 * Shared by the core and the feature modules, hence under ui/ rather than in
 * one module.
 *
 * Committed on blur or Enter, discarded on Escape. Deliberately not a form:
 * renaming a task is a single value, and a dialog for it would cost more
 * clicks than the rename is worth.
 */
export function EditableText({
  value,
  onCommit,
  bold,
  dimmed,
  monospace,
  placeholder,
  title,
  size = 'sm',
  allowEmpty = false,
}: {
  value: string;
  onCommit: (next: string) => void;
  bold?: boolean;
  dimmed?: boolean;
  monospace?: boolean;
  placeholder?: string;
  title?: string;
  /** Text size of the label; a project title in a header wants more than a cell. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** For optional fields: committing an empty value clears it. */
  allowEmpty?: boolean;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  // After editing, focus returns to the cell, so keyboard users continue
  // with Tab from where they were instead of from the top of the page.
  const displayRef = useRef<HTMLParagraphElement>(null);
  const refocus = useRef(false);
  useEffect(() => {
    if (!editing && refocus.current) {
      refocus.current = false;
      displayRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = (): void => {
    setEditing(false);
    const trimmed = draft.trim();
    if ((trimmed.length > 0 || allowEmpty) && trimmed !== value) onCommit(trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            refocus.current = true;
            commit();
          }
          if (event.key === 'Escape') {
            refocus.current = true;
            setDraft(value);
            setEditing(false);
          }
        }}
        style={{
          width: '100%',
          minWidth: 0,
          border: '1px solid var(--mantine-color-brand-5)',
          borderRadius: 4,
          padding: '1px 4px',
          font: 'inherit',
          fontFamily: monospace ? 'monospace' : 'inherit',
          fontWeight: bold ? 600 : 400,
          fontSize: `var(--mantine-font-size-${size})`,
          background: 'var(--mantine-color-body)',
          color: 'var(--mantine-color-text)',
        }}
      />
    );
  }

  return (
    <Text
      ref={displayRef}
      size={size}
      fw={bold ? 600 : 400}
      c={dimmed || value.length === 0 ? 'dimmed' : undefined}
      ff={monospace ? 'monospace' : undefined}
      title={title ?? 'Zum Umbenennen anklicken'}
      className="pst-cell"
      role="button"
      tabIndex={0}
      onClick={() => setEditing(true)}
      onKeyDown={(event) => {
        // Enter or F2 opens the cell, as in a spreadsheet.
        if (event.key === 'Enter' || event.key === 'F2') {
          event.preventDefault();
          setEditing(true);
        }
      }}
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      }}
    >
      {value.length > 0 ? value : (placeholder ?? '—')}
    </Text>
  );
}
